import type { Persona, PersonaMetadata } from "@/types/api/Persona"
import {
  COVAL_VOICES,
  DEFAULT_COVAL_VOICE,
  DEFAULT_LANGUAGE_CODE,
  DEFAULT_NEW_PERSONA_VOICE,
  SUPPORTED_LANGUAGES,
} from "@/types/ModelConfigs/CovalVoiceConstants"
import type { VoicePersonaConfig } from "@/types/ModelConfigs/VoiceModelConfigs"
import {
  AVATAR_COLORS,
  getAvatarUrl,
  type FaceCustomization,
} from "@/utils/avatarIdentity"

import type { PersonaFormData } from "./personaValidation"

export const HAIR_VARIANTS = Array.from(
  { length: 63 },
  (_, index) => `variant${String(index + 1).padStart(2, "0")}`,
)

export const EYES_VARIANTS = Array.from(
  { length: 5 },
  (_, index) => `variant${String(index + 1).padStart(2, "0")}`,
)

export const LIPS_VARIANTS = Array.from(
  { length: 30 },
  (_, index) => `variant${String(index + 1).padStart(2, "0")}`,
)

const DEFAULT_FACE_CUSTOMIZATION: FaceCustomization = {
  hair: HAIR_VARIANTS[0],
  eyes: EYES_VARIANTS[0],
  lips: LIPS_VARIANTS[0],
}

export const DEFAULT_PERSONA_TEMPLATE = ""

export const getAvatarSeedFromUrl = (avatarUrl?: string) =>
  avatarUrl?.split("seed=")[1]?.split("&")[0]

const getAvatarPreviewSeedFromUrl = (avatarUrl?: string) =>
  getAvatarSeedFromUrl(avatarUrl) || "default"

const parseFaceCustomizationFromUrl = (url: string): FaceCustomization => {
  const urlParams = new URLSearchParams(url.split("?")[1])

  return {
    hair: urlParams.get("hair") || DEFAULT_FACE_CUSTOMIZATION.hair,
    eyes: urlParams.get("eyes") || DEFAULT_FACE_CUSTOMIZATION.eyes,
    lips: urlParams.get("lips") || DEFAULT_FACE_CUSTOMIZATION.lips,
  }
}

export const createDefaultPersonaFormData = (
  avatarUrl = getAvatarUrl(),
): PersonaFormData => ({
  display_name: "",
  metadata: {
    conversation_initiator: "agent",
    voice_name: DEFAULT_NEW_PERSONA_VOICE,
    language_code: DEFAULT_LANGUAGE_CODE,
    interruption_rate: "NONE",
  },
  simulated_user_prompt: DEFAULT_PERSONA_TEMPLATE,
  avatar_url: avatarUrl,
  face_customization: parseFaceCustomizationFromUrl(avatarUrl),
})

export const createPersonaFormDataFromNormalizedPersona = (
  normalizedPersona: Persona,
  avatarUrl = normalizedPersona.avatar_url || getAvatarUrl(),
): PersonaFormData => {
  const voiceMetadata = normalizedPersona.metadata as
    | VoicePersonaConfig
    | undefined
  const hasCustomVoice = Boolean(normalizedPersona.metadata?.custom_voice_id)
  const voiceName =
    normalizedPersona.voice_name ||
    normalizedPersona.metadata?.voice_name ||
    (hasCustomVoice ? undefined : DEFAULT_COVAL_VOICE)
  const languageCode =
    normalizedPersona.language_code ||
    normalizedPersona.metadata?.language_code ||
    DEFAULT_LANGUAGE_CODE

  return {
    display_name: normalizedPersona.display_name || "",
    metadata: {
      ...normalizedPersona.metadata,
      voice_name: voiceName,
      language_code: languageCode,
      conversation_initiator: voiceMetadata?.conversation_initiator || "agent",
      interruption_rate: voiceMetadata?.interruption_rate || "NONE",
    },
    simulated_user_prompt: normalizedPersona.simulated_user_prompt || "",
    avatar_url: avatarUrl,
    face_customization: parseFaceCustomizationFromUrl(avatarUrl),
  }
}

export const buildFaceCustomizationPreviewUrls = ({
  avatarUrl,
  feature,
  faceCustomization,
  variants,
}: {
  avatarUrl: string
  feature: keyof FaceCustomization
  faceCustomization: FaceCustomization
  variants: string[]
}) => {
  const previewSeed = getAvatarPreviewSeedFromUrl(avatarUrl)

  return variants.map((variant) =>
    getAvatarUrl(previewSeed, {
      ...faceCustomization,
      [feature]: variant,
    }),
  )
}

export const getRandomAvatarColorIndex = (randomValue = Math.random()) =>
  Math.floor(randomValue * AVATAR_COLORS.length)

export const getAvailableVoices = (languageCode?: string) =>
  COVAL_VOICES.filter((voice) => voiceSupportsLanguageCode(voice, languageCode))

export const getAvailableLanguages = (voiceName?: string) => {
  if (!voiceName) {
    return SUPPORTED_LANGUAGES
  }

  const selectedVoice = COVAL_VOICES.find((voice) => voice.name === voiceName)

  if (!selectedVoice?.languages || selectedVoice.languages.length === 0) {
    return SUPPORTED_LANGUAGES
  }

  return SUPPORTED_LANGUAGES.filter((language) =>
    voiceSupportsLanguageCode(selectedVoice, language),
  )
}

export const voiceSupportsLanguageCode = (
  voice: { languages?: readonly string[] },
  languageCode?: string,
) => {
  if (!languageCode) {
    return true
  }

  if (!voice.languages || voice.languages.length === 0) {
    return true
  }

  if (voice.languages.includes(languageCode)) {
    return true
  }

  return false
}

export const isVoiceLanguageCombinationSupported = (
  voiceName?: string,
  languageCode?: string,
) => {
  if (!voiceName || !languageCode) {
    return false
  }

  const selectedVoice = COVAL_VOICES.find((voice) => voice.name === voiceName)
  if (!selectedVoice) {
    return false
  }

  return voiceSupportsLanguageCode(selectedVoice, languageCode)
}

export const getMetadataForVoiceSelection = (
  metadata: PersonaMetadata | undefined,
  selectedVoiceName?: string,
) => {
  const currentLanguageCode = metadata?.language_code
  let shouldClearLanguage = false

  if (selectedVoiceName && currentLanguageCode) {
    const newVoice = COVAL_VOICES.find(
      (voice) => voice.name === selectedVoiceName,
    )

    if (
      newVoice?.languages &&
      newVoice.languages.length > 0 &&
      !voiceSupportsLanguageCode(newVoice, currentLanguageCode)
    ) {
      shouldClearLanguage = true
    }
  }

  const nextMetadata: PersonaMetadata = {
    ...metadata,
    voice_name: selectedVoiceName,
    ...(shouldClearLanguage && { language_code: undefined }),
    // A built-in voice replaces any designed custom voice. Clearing happens
    // only on selection — deselecting a voice leaves custom voice fields alone.
    ...(selectedVoiceName && {
      custom_voice_id: undefined,
      custom_voice_name: undefined,
      custom_voice_description: undefined,
      custom_voice_language: undefined,
    }),
  }

  return nextMetadata
}

export const getMetadataForLanguageSelection = (
  metadata: PersonaMetadata | undefined,
  selectedLanguageCode?: string,
) => {
  const currentVoiceName = metadata?.voice_name
  let shouldClearVoice = false

  if (selectedLanguageCode && currentVoiceName) {
    const currentVoice = COVAL_VOICES.find(
      (voice) => voice.name === currentVoiceName,
    )

    if (
      currentVoice?.languages &&
      currentVoice.languages.length > 0 &&
      !voiceSupportsLanguageCode(currentVoice, selectedLanguageCode)
    ) {
      shouldClearVoice = true
    }
  }

  return {
    ...metadata,
    language_code: selectedLanguageCode,
    ...(shouldClearVoice && { voice_name: undefined }),
  }
}
